import {
  point2d,
  vertex,
  HatchBoundaryPaths,
  HatchPolylineBoundary,
  HatchEdgesTypeData,
  HatchPredefinedPatterns,
  pattern
} from '@tarikjabiri/dxf';

/**
 * HatchUtils - Utility functions for processing and converting hatch boundary data
 * for DXF export. Handles conversion from application boundary format to DXF library format.
 */

/**
 * Constants for angle conversion and validation
 */
export const DEGREES_TO_RADIANS = Math.PI / 180;
export const MIN_BOUNDARY_POINTS = 3;

/**
 * AR Pattern Definitions - matches AutoCAD's acad.pat patterns
 * These are used to inject pattern line definitions into DXF when using AR-* patterns
 */
export const AR_PATTERN_DEFINITIONS = {
  'AR-B816': [
    { angle: 45, x: 0, y: 0, offsetX: 0, offsetY: 3.175, dashes: [] },
    { angle: 135, x: 0, y: 0, offsetX: 6.35, offsetY: 3.175, dashes: [7.9375, -4.7625] }
  ],
  'AR-B816C': [
    { angle: 0, x: 0, y: 0, offsetX: 0, offsetY: 203.2, dashes: [] },
    { angle: 90, x: 0, y: 0, offsetX: 203.2, offsetY: 203.2, dashes: [203.2, -203.2] }
  ],
  'AR-B88': [
    { angle: 0, x: 0, y: 0, offsetX: 203.2, offsetY: 203.2, dashes: [396.875, -9.525] },
    { angle: 0, x: -203.2, y: 9.525, offsetX: 203.2, offsetY: 203.2, dashes: [396.875, -9.525] },
    { angle: 90, x: 0, y: 0, offsetX: 203.2, offsetY: 203.2, dashes: [-212.725, 193.675] },
    { angle: 90, x: -9.525, y: 0, offsetX: 203.2, offsetY: 203.2, dashes: [-212.725, 193.675] }
  ],
  'AR-BRELM': [
    { angle: 0, x: 0, y: 0, offsetX: 0, offsetY: 203.2, dashes: [] },
    { angle: 90, x: 0, y: 0, offsetX: 203.2, offsetY: 101.6, dashes: [203.2, -203.2] }
  ],
  'AR-BRSTD': [
    { angle: 0, x: 0, y: 0, offsetX: 0, offsetY: 135.484, dashes: [193.675, -9.525] },
    { angle: 0, x: 0, y: 57.15, offsetX: 0, offsetY: 135.484, dashes: [193.675, -9.525] },
    { angle: 0, x: 50.8, y: 67.7418, offsetX: 0, offsetY: 135.484, dashes: [92.075, -9.525] },
    { angle: 0, x: 50.8, y: 124.892, offsetX: 0, offsetY: 135.484, dashes: [92.075, -9.525] },
    { angle: 90, x: 0, y: 0, offsetX: 0, offsetY: 203.2, dashes: [57.15, -78.334] },
    { angle: 90, x: -9.525, y: 0, offsetX: 0, offsetY: 203.2, dashes: [57.15, -78.334] },
    { angle: 90, x: 50.8, y: 67.7418, offsetX: 0, offsetY: 101.6, dashes: [57.15, -78.334] },
    { angle: 90, x: 41.275, y: 67.7418, offsetX: 0, offsetY: 101.6, dashes: [57.15, -78.334] }
  ],
  'AR-CONC': [
    { angle: 0, x: 0, y: 0, offsetX: 0, offsetY: 67.7418, dashes: [] },
    { angle: 90, x: 0, y: 0, offsetX: 67.7418, offsetY: 101.6, dashes: [67.7418, -67.7418] }
  ],
  'AR-HBONE': [
    { angle: 45, x: 0, y: 0, offsetX: 101.6, offsetY: 101.6, dashes: [101.6, -203.2] },
    { angle: 135, x: 0, y: 0, offsetX: 101.6, offsetY: 101.6, dashes: [101.6, -203.2] }
  ],
  'AR-PARQ1': [
    { angle: 90, x: 0, y: 0, offsetX: 304.8, offsetY: 304.8, dashes: [304.8, -304.8] },
    { angle: 90, x: 50.8, y: 0, offsetX: 304.8, offsetY: 304.8, dashes: [304.8, -304.8] },
    { angle: 90, x: 101.6, y: 0, offsetX: 304.8, offsetY: 304.8, dashes: [304.8, -304.8] },
    { angle: 90, x: 152.4, y: 0, offsetX: 304.8, offsetY: 304.8, dashes: [304.8, -304.8] },
    { angle: 90, x: 203.2, y: 0, offsetX: 304.8, offsetY: 304.8, dashes: [304.8, -304.8] },
    { angle: 90, x: 254, y: 0, offsetX: 304.8, offsetY: 304.8, dashes: [304.8, -304.8] },
    { angle: 0, x: 0, y: 304.8, offsetX: 304.8, offsetY: 304.8, dashes: [304.8, -304.8] },
    { angle: 0, x: 0, y: 355.6, offsetX: 304.8, offsetY: 304.8, dashes: [304.8, -304.8] },
    { angle: 0, x: 0, y: 406.4, offsetX: 304.8, offsetY: 304.8, dashes: [304.8, -304.8] },
    { angle: 0, x: 0, y: 457.2, offsetX: 304.8, offsetY: 304.8, dashes: [304.8, -304.8] },
    { angle: 0, x: 0, y: 508, offsetX: 304.8, offsetY: 304.8, dashes: [304.8, -304.8] },
    { angle: 0, x: 0, y: 558.8, offsetX: 304.8, offsetY: 304.8, dashes: [304.8, -304.8] }
  ],
  'AR-RROOF': [
    { angle: 0, x: 0, y: 0, offsetX: 317.5, offsetY: 317.5, dashes: [] },
    { angle: 0, x: 158.75, y: 158.75, offsetX: 317.5, offsetY: 317.5, dashes: [] },
    { angle: 90, x: 0, y: 0, offsetX: 508, offsetY: 158.75, dashes: [158.75, -158.75] }
  ],
  'AR-RSHKE': [
    { angle: 0, x: 0, y: 0, offsetX: 317.5, offsetY: 635, dashes: [] },
    { angle: 0, x: 317.5, y: 317.5, offsetX: 635, offsetY: 635, dashes: [] },
    { angle: 90, x: 0, y: 0, offsetX: 793.75, offsetY: 317.5, dashes: [317.5, -317.5] },
    { angle: 90, x: 317.5, y: 317.5, offsetX: 1111.25, offsetY: 317.5, dashes: [317.5, -317.5] }
  ],
  'AR-SAND': [
    { angle: 37.5, x: 0, y: 0, offsetX: 1.3716, offsetY: 13.716, dashes: [0, -13.716, 0, -6.858, 0, -10.287] },
    { angle: 7.5, x: 0, y: 0, offsetX: 1.3716, offsetY: 19.202, dashes: [0, -6.858, 0, -10.287, 0, -6.858] }
  ]
};

/**
 * Generates DXF pattern definition lines for AR patterns
 * @param {string} patternName - Pattern name (e.g., AR-B816C)
 * @returns {string} - DXF group codes for pattern definition
 */
export const generateARPatternDxfLines = (patternName) => {
  const lines = AR_PATTERN_DEFINITIONS[patternName];
  if (!lines) return null;

  let dxf = '';
  // 78 = Number of pattern definition lines
  dxf += `78\n${lines.length}\n`;

  lines.forEach(line => {
    // 53 = Pattern line angle
    dxf += `53\n${line.angle}\n`;
    // 43 = Pattern line base point X
    dxf += `43\n${line.x}\n`;
    // 44 = Pattern line base point Y
    dxf += `44\n${line.y}\n`;
    // 45 = Pattern line offset X
    dxf += `45\n${line.offsetX}\n`;
    // 46 = Pattern line offset Y
    dxf += `46\n${line.offsetY}\n`;
    // 79 = Number of dash length items
    dxf += `79\n${line.dashes.length}\n`;
    // 49 = Dash length (repeated for each dash)
    line.dashes.forEach(dash => {
      dxf += `49\n${dash}\n`;
    });
  });

  return dxf;
};

/**
 * Validates if an edge has required line properties
 * @param {Object} edge - The edge object to validate
 * @returns {boolean} - True if edge has valid line properties
 */
export const isValidLineEdge = (edge) => {
  return edge.edgeType === 'Line' && 
         typeof edge.x1 === 'number' && 
         typeof edge.y1 === 'number' && 
         typeof edge.x2 === 'number' && 
         typeof edge.y2 === 'number';
};

/**
 * Validates if an edge has required arc properties
 * @param {Object} edge - The edge object to validate
 * @returns {boolean} - True if edge has valid arc properties
 */
export const isValidArcEdge = (edge) => {
  return edge.edgeType === 'CircularArc' && 
         typeof edge.x === 'number' && 
         typeof edge.y === 'number' && 
         typeof edge.radius === 'number' &&
         typeof edge.StartAngle === 'number' &&
         typeof edge.EndAngle === 'number';
};

/**
 * Creates a polyline boundary from line edges
 * @param {Array} edges - Array of line edges
 * @returns {HatchPolylineBoundary} - DXF polyline boundary
 */
export const createPolylineBoundaryFromEdges = (edges) => {
  const polylineBoundary = new HatchPolylineBoundary();
  
  if (edges.length === 0) return polylineBoundary;
  
  // Add first point
  const firstEdge = edges[0];
  polylineBoundary.add(vertex(firstEdge.x1, firstEdge.y1));
  
  // Add remaining points
  edges.forEach(edge => {
    polylineBoundary.add(vertex(edge.x2, edge.y2));
  });
  
  return polylineBoundary;
};

/**
 * Creates an edges boundary from mixed edge types
 * @param {Array} edges - Array of edges (lines, arcs, etc.)
 * @returns {HatchEdgesTypeData|null} - DXF edges boundary or null if no valid edges
 */
export const createEdgesBoundaryFromEdges = (edges) => {
  const edgesBoundary = new HatchEdgesTypeData();
  
  edges.forEach(edge => {
    if (isValidLineEdge(edge)) {
      edgesBoundary.addLineEdgeData(
        point2d(edge.x1, edge.y1),
        point2d(edge.x2, edge.y2)
      );
    } else if (isValidArcEdge(edge)) {
      edgesBoundary.addArcEdgeData(
        point2d(edge.x, edge.y),
        edge.radius,
        edge.StartAngle * DEGREES_TO_RADIANS,
        edge.EndAngle * DEGREES_TO_RADIANS,
        !edge.IsCounterclockwiseFlag
      );
    } else {
      console.warn(`Unsupported edge type for export: ${edge.edgeType}`);
    }
  });
  
  return edgesBoundary.edgesData.length > 0 ? edgesBoundary : null;
};

/**
 * Creates polyline boundary from points array
 * @param {Object} group - Group object with points data
 * @returns {HatchPolylineBoundary|null} - DXF polyline boundary or null if invalid
 */
export const createPolylineBoundaryFromPoints = (group) => {
  const polylineBoundary = new HatchPolylineBoundary();
  
  if (!group.points || !Array.isArray(group.points)) {
    return null;
  }
  
  if (group.bulge && group.NumberOfPolylineVertices) {
    // Handle polyline with bulges (arcs between vertices)
    for (let i = 0; i < group.NumberOfPolylineVertices; i++) {
      const x = group.points[i * 3];
      const y = group.points[i * 3 + 1];
      const bulgeValue = group.points[i * 3 + 2] || 0;
      
      if (typeof x === 'number' && typeof y === 'number') {
        polylineBoundary.add(vertex(x, y, bulgeValue));
      }
    }
  } else {
    // Handle simple polyline (x,y pairs)
    for (let i = 0; i < group.points.length; i += 2) {
      const x = group.points[i];
      const y = group.points[i + 1];
      
      if (typeof x === 'number' && typeof y === 'number') {
        polylineBoundary.add(vertex(x, y));
      }
    }
  }
  
  return polylineBoundary.vertices.length > 0 ? polylineBoundary : null;
};

/**
 * Processes a single boundary group and adds it to the DXF boundary
 * @param {Array|Object} group - The boundary group to process
 * @param {HatchBoundaryPaths} dxfBoundary - The DXF boundary to add to
 */
export const processBoundaryGroup = (group, dxfBoundary) => {
  if (Array.isArray(group)) {
    // Array of edges
    const canUsePolyline = group.every(edge => isValidLineEdge(edge));
    
    if (canUsePolyline && group.length > 0) {
      const polylineBoundary = createPolylineBoundaryFromEdges(group);
      dxfBoundary.addPolylineBoundary(polylineBoundary);
    } else {
      const edgesBoundary = createEdgesBoundaryFromEdges(group);
      if (edgesBoundary) {
        dxfBoundary.addEdgesTypeData(edgesBoundary);
      }
    }
  } else if (group && typeof group === 'object') {
    // Single edge object
    if (isValidLineEdge(group)) {
      const edgesBoundary = new HatchEdgesTypeData();
      edgesBoundary.addLineEdgeData(
        point2d(group.x1, group.y1),
        point2d(group.x2, group.y2)
      );
      dxfBoundary.addEdgesTypeData(edgesBoundary);
    } else if ((group.edgeType === 'Line' || group.edgeType === 'Polyline') && group.points) {
      const polylineBoundary = createPolylineBoundaryFromPoints(group);
      if (polylineBoundary) {
        dxfBoundary.addPolylineBoundary(polylineBoundary);
      }
    } else {
      console.warn('Unsupported group format:', group.edgeType || 'unknown');
    }
  } else {
    console.warn('Invalid group type - neither array nor object:', typeof group);
  }
};

/**
 * Converts app boundary paths to DXF library HatchBoundaryPaths
 * @param {Array} boundaryPaths - App boundary paths structure
 * @returns {HatchBoundaryPaths} - DXF library boundary paths
 */
export const convertBoundaryPaths = (boundaryPaths) => {
  const dxfBoundary = new HatchBoundaryPaths();
  
  if (!boundaryPaths || !Array.isArray(boundaryPaths)) {
    console.warn('Invalid boundary paths for hatch export');
    return dxfBoundary;
  }

  boundaryPaths.forEach(boundary => {
    if (!boundary || !['outer', 'inner'].includes(boundary.type) || !boundary.groups) {
      console.warn('Invalid boundary - missing type or groups:', boundary?.type || 'undefined');
      return;
    }

    boundary.groups.forEach(group => {
      try {
        processBoundaryGroup(group, dxfBoundary);
      } catch (error) {
        console.error('Error processing boundary group:', error);
      }
    });
  });
  
  return dxfBoundary;
};

/**
 * Creates appropriate fill pattern based on hatch element data
 * @param {Object} element - Hatch element with pattern information
 * @returns {Object} - Pattern or gradient fill object
 */
export const createHatchFill = (element) => {
  const {
    SolidFillFlag = 1,
    HatchPatternName = "SOLID",
    HatchPatternScale = 1,
    HatchPatternAngle = 0
  } = element;

  const isSolid = SolidFillFlag === 1 && HatchPatternName === "SOLID";

  if (isSolid) {
    return pattern({
      name: HatchPredefinedPatterns.SOLID,
      scale: HatchPatternScale,
      angle: HatchPatternAngle
    });
  }

  // For non-solid patterns, use predefined pattern if available
  const predefinedPattern = HatchPredefinedPatterns[HatchPatternName];

  if (predefinedPattern) {
    return pattern({
      name: predefinedPattern,
      scale: HatchPatternScale,
      angle: HatchPatternAngle,
      double: false
    });
  }

  // Pattern kütüphanede yoksa, adını doğrudan kullan (AR-*, custom patterns vb.)
  // SOLID'e fallback YAPMA - orijinal pattern adını koru
  return pattern({
    name: HatchPatternName,
    scale: HatchPatternScale,
    angle: HatchPatternAngle,
    double: false
  });
};

/**
 * Creates a simple solid hatch from an array of points
 * @param {Array} points - Array of point objects with x, y coordinates
 * @returns {Object|null} - Boundary paths and fill pattern or null if invalid
 */
export const createSolidHatchFromPoints = (points) => {
  if (!points?.length || points.length < MIN_BOUNDARY_POINTS) {
    return null;
  }

  const polylineBoundary = new HatchPolylineBoundary();
  points.forEach(point => polylineBoundary.add(vertex(point.x, point.y)));
  
  const boundaryPaths = new HatchBoundaryPaths();
  boundaryPaths.addPolylineBoundary(polylineBoundary);
  
  const solidFill = pattern({ name: HatchPredefinedPatterns.SOLID });
  
  return { boundaryPaths, fill: solidFill };
}; 